import axios from 'axios';
import type { DateRange } from '@/lib/date-input';
import { api } from './api';
import type { UploadProgress } from './media.service';

export type StreamVideoStatus = 'UPLOADING' | 'PROCESSING' | 'READY' | 'FAILED';
export type StreamVisibility = 'PRIVATE' | 'SHAREABLE' | 'PUBLIC';
export type StreamWatermarkText =
  'NONE' | 'USER_EMAIL' | 'TENANT_NAME' | 'VIDEO_ID' | 'CUSTOM';
export type StreamWatermarkPosition =
  'TOP_RIGHT' | 'BOTTOM_RIGHT' | 'BOTTOM_LEFT' | 'TOP_LEFT' | 'FLOATING';

export interface StreamSubtitle {
  lang: string;
  label: string;
  url: string;
}

export interface StreamVideoUsage {
  storageBytes: number;
  processedMinutes: number;
  sessions: number;
  viewers: number;
  egressBytes: number;
  estimatedBytes: number;
  cost: {
    delivery: number;
    storage: number;
    processing: number;
    total: number;
  };
  modelCost: number;
  real: {
    delivery: number;
    storage: number;
    processing: number;
    total: number;
  } | null;
  realShare: number;
  costSource: 'real' | 'mixed' | 'estimate';
}

export interface StreamVideo {
  id: string;
  title: string;
  description: string;
  visibility: StreamVisibility;
  watermarkText: StreamWatermarkText;
  watermarkCustom: string | null;
  watermarkPosition: StreamWatermarkPosition;
  status: StreamVideoStatus;
  error: string | null;
  durationMs: number | null;
  width: number | null;
  height: number | null;
  renditions: number[];
  thumbnailUrl: string | null;
  thumbnailCandidates: string[];
  subtitles: StreamSubtitle[];
  views?: number;
  watchedSeconds?: number;
  lessonsCount?: number;
  usage?: StreamVideoUsage;
  createdAt: string;
  updatedAt: string;
  readyAt: string | null;
}

export interface StreamPage<T> {
  data: T[];
  meta: { total: number; page: number; perPage: number };
}

export interface StreamEmbed {
  embedUrl: string;
  expiresAt: string;
}

export interface StreamStats {
  sessions: number;
  viewers: number;
  watchedSeconds: number;
  avgCoveredSeconds: number;
  completedViewers: number;
  durationMs: number | null;
  avgCoverage: number;
}

export interface StreamViewerStats {
  viewerId: string;
  viewerName: string | null;
  sessions: number;
  watchedSeconds: number;
  coveredSeconds: number;
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface StreamSession {
  id: string;
  viewerId: string;
  viewerName: string | null;
  viewerEmail: string | null;
  startedAt: string;
  lastSeenAt: string | null;
  endedAt: string | null;
  endReason: 'ENDED' | 'EXPIRED' | 'REPLACED' | null;
  watchedSeconds: number;
  coveredSeconds: number;
  lastPosition: number;
  embedOrigin: string | null;
  ip: string | null;
  browser: string | null;
  os: string | null;
  device: string | null;
  country: string | null;
  city: string | null;
}

interface UploadSession {
  video: StreamVideo;
  upload: { url: string; method: string };
}

export interface StreamUsageCost {
  storage: number;
  processing: number;
  delivery: number;
  total: number;
}

export interface StreamUsage {
  period: { from: string; to: string };
  storage: {
    videos: number;
    originalsBytes: number;
    mediaBytes: number;
    totalBytes: number;
  };
  processing: {
    videos: number;
    sourceMinutes: number;
    outputMinutes: { sd: number; hd: number; uhd: number };
  };
  playback: {
    sessions: number;
    viewers: number;
    watchedMinutes: number;
    egress: {
      measuredBytes: number;
      estimatedBytes: number;
      totalBytes: number;
      requests: number;
    };
    estimatedEgressBytes: number;
  };
  cost: StreamUsageCost;
}

export type StreamCostSource = 'real' | 'mixed' | 'estimate';

export interface StreamCostCategory {
  category: string;
  displayUsd: number;
  realUsd: number;
  estimatedUsd: number;
}

export interface StreamCosts {
  hasReal: boolean;
  period: { from: string; to: string };
  asOf: string | null;
  lastSyncAt: string | null;
  sourceStatus: Record<string, string>;
  status: 'provisional' | 'final';
  totals: {
    displayUsd: number;
    realUsd: number;
    estimatedUsd: number;
    realShare: number;
    modelUsd: number;
  };
  categories: StreamCostCategory[];
  daily: {
    day: string;
    displayUsd: number;
    realUsd: number;
    estimatedUsd: number;
    status: string;
  }[];
}

export interface StreamViewerVideoUsage {
  videoId: string;
  title: string | null;
  durationMs: number | null;
  sessions: number;
  watchedSeconds: number;
  coveredSeconds: number;
  progress: number;
  lastPosition: number;
  lastSeenAt: string | null;
  egressBytes: number;
  estimatedBytes: number;
  cost: number;
  modelCost: number;
  realCost: number | null;
  realShare: number;
  costSource: StreamCostSource;
  charge: number | null;
}

export interface StreamViewerUsage {
  viewerId: string;
  name: string | null;
  email: string | null;
  sessions: number;
  watchedSeconds: number;
  lastSeenAt: string | null;
  egressBytes: number;
  estimatedBytes: number;
  cost: number;
  modelCost: number;
  realCost: number | null;
  realShare: number;
  costSource: StreamCostSource;
  charge: number | null;
  videos: StreamViewerVideoUsage[];
}

export interface StreamViewersUsage {
  period: { from: string; to: string };
  markup: number;
  totals: {
    viewers: number;
    watchedSeconds: number;
    egressBytes: number;
    estimatedBytes: number;
    delivery: number;
    storage: number;
    processing: number;
    cost: number;
    modelCost: number;
    costSource: StreamCostSource;
    realCost: number | null;
    realDelivery: number | null;
    realStorage: number | null;
    realProcessing: number | null;
    charge: number | null;
  };
  real: {
    asOf: string | null;
    status: 'provisional' | 'final';
    displayUsd: number;
    realUsd: number;
    estimatedUsd: number;
    realShare: number;
  } | null;
  viewers: StreamViewerUsage[];
  meta: { total: number; page: number; perPage: number };
}

export interface StreamTenantCosts extends StreamCosts {
  tenantId: string;
  name: string;
}

export interface StreamCostsByTenant {
  asOf: string | null;
  platform: StreamCosts['totals'];
  platformHasReal: boolean;
  platformCategories: StreamCostCategory[];
  data: StreamTenantCosts[];
}

export interface StreamTenantUsage extends StreamUsage {
  tenantId: string;
  name: string;
}

async function sendFile(
  session: UploadSession,
  file: File,
  onProgress?: (progress: UploadProgress) => void,
): Promise<StreamVideo> {
  await axios.put(session.upload.url, file, {
    headers: { 'Content-Type': file.type },
    timeout: 0,
    onUploadProgress: (event) => {
      if (onProgress && event.total) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percentage: Math.round((event.loaded * 100) / event.total),
        });
      }
    },
  });
  return session.video;
}

function unwrap<T>(body: unknown): T {
  const value = body as { data?: T } | T;
  return (value as { data?: T })?.data ?? (value as T);
}

function unwrapPage<T>(body: unknown): StreamPage<T> {
  const value = body as { data: T[]; meta: StreamPage<T>['meta'] };
  return { data: value?.data ?? [], meta: value?.meta };
}

export const streamService = {
  async lessonPlayback(lessonId: string): Promise<StreamEmbed> {
    const { data } = await api.post(`/lessons/${lessonId}/playback`);
    return unwrap(data);
  },

  async lessonSync(lessonId: string): Promise<{ completed: boolean } | null> {
    const { data } = await api.post(`/lessons/${lessonId}/playback/sync`);
    return unwrap(data);
  },

  async preview(id: string): Promise<StreamEmbed> {
    const { data } = await api.post(`/stream/videos/${id}/preview`);
    return unwrap(data);
  },

  async list(params: {
    page?: number;
    perPage?: number;
    status?: StreamVideoStatus;
    q?: string;
    from?: string;
    to?: string;
  }): Promise<StreamPage<StreamVideo>> {
    const { data } = await api.get('/stream/videos', { params });
    return unwrapPage(data);
  },

  async get(id: string, range?: DateRange): Promise<StreamVideo> {
    const { data } = await api.get(`/stream/videos/${id}`, { params: range });
    return unwrap(data);
  },

  async upload(
    file: File,
    title: string,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<StreamVideo> {
    const { data } = await api.post('/stream/videos', {
      title,
      contentType: file.type,
      origin: window.location.origin,
    });
    return sendFile(unwrap<UploadSession>(data), file, onProgress);
  },

  async reupload(
    id: string,
    file: File,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<StreamVideo> {
    const { data } = await api.post(`/stream/videos/${id}/upload`, {
      contentType: file.type,
      origin: window.location.origin,
    });
    return sendFile(unwrap<UploadSession>(data), file, onProgress);
  },

  async usage(range?: DateRange): Promise<StreamUsage> {
    const { data } = await api.get('/stream/usage', { params: range });
    return unwrap(data);
  },

  async usageByTenant(
    range?: DateRange,
  ): Promise<{ data: StreamTenantUsage[] }> {
    const { data } = await api.get('/stream/usage/tenants', { params: range });
    return unwrap(data);
  },

  async update(
    id: string,
    body: Partial<{
      title: string;
      description: string;
      visibility: StreamVisibility;
      watermarkText: StreamWatermarkText;
      watermarkCustom: string;
      watermarkPosition: StreamWatermarkPosition;
      thumbnail: number;
    }>,
  ): Promise<StreamVideo> {
    const { data } = await api.patch(`/stream/videos/${id}`, body);
    return unwrap(data);
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/stream/videos/${id}`);
  },

  async thumbnailFromFrame(id: string, atMs: number): Promise<StreamVideo> {
    const { data } = await api.post(`/stream/videos/${id}/thumbnail/frame`, {
      atMs,
    });
    return unwrap(data);
  },

  async uploadThumbnail(id: string, file: File): Promise<StreamVideo> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post(`/stream/videos/${id}/thumbnail`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap(data);
  },

  async putSubtitle(
    id: string,
    lang: string,
    label: string,
    file: File,
  ): Promise<StreamVideo> {
    const form = new FormData();
    form.append('file', file);
    form.append('label', label);
    const { data } = await api.post(
      `/stream/videos/${id}/subtitles/${encodeURIComponent(lang)}`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return unwrap(data);
  },

  async deleteSubtitle(id: string, lang: string): Promise<void> {
    await api.delete(
      `/stream/videos/${id}/subtitles/${encodeURIComponent(lang)}`,
    );
  },

  async viewersUsage(params: {
    from?: string;
    to?: string;
    q?: string;
    page?: number;
    perPage?: number;
  }): Promise<StreamViewersUsage> {
    const { data } = await api.get('/stream/usage/viewers', { params });
    return unwrap(data);
  },

  async costs(range?: DateRange): Promise<StreamCosts> {
    const { data } = await api.get('/stream/costs', { params: range });
    return unwrap(data);
  },

  async costsByTenant(range?: DateRange): Promise<StreamCostsByTenant> {
    const { data } = await api.get('/stream/costs/tenants', { params: range });
    return unwrap(data);
  },

  async syncCosts(): Promise<void> {
    await api.post('/stream/costs/sync');
  },

  async stats(id: string): Promise<StreamStats> {
    const { data } = await api.get(`/stream/videos/${id}/stats`);
    return unwrap(data);
  },

  async viewers(
    id: string,
    params: { page?: number; perPage?: number },
  ): Promise<StreamPage<StreamViewerStats>> {
    const { data } = await api.get(`/stream/videos/${id}/viewers`, {
      params,
    });
    return unwrapPage(data);
  },

  async sessions(
    id: string,
    params: { page?: number; perPage?: number; viewerId?: string },
  ): Promise<StreamPage<StreamSession>> {
    const { data } = await api.get(`/stream/videos/${id}/sessions`, {
      params,
    });
    return unwrapPage(data);
  },
};
